import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { voiceApi } from '../../api/voice'
import { RefusedError } from '../../api/client'
import { Correct } from '../../components/Correct'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import type { ExemplarView } from '../../types/voice'

// One of the person's own messages, verbatim. Long ones (a pasted list, a forwarded log) are
// clipped to a readable height with the rest a click away, so one message never owns the page.
//
// Two things can be done to it. It can be CORRECTED — the scenario it was filed under is an
// assertion like any other, and until REQ-061 it was the only kind of assertion on the screen that
// could not be argued with. And it can be REWRITTEN: "this is what I would rather have written",
// which is the strongest voice signal there is, and what Don had already spent an evening doing
// before he asked whether it counted. The sent original is never overwritten — it is the record of
// what went out — so a rewritten card shows the rewrite and keeps the original one click away.
const CLIP_LINES = 14

export function Exemplar({ e, showOriginal = false }: { e: ExemplarView; showOriginal?: boolean }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [original, setOriginal] = useState(showOriginal)
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(e.rewriteText ?? e.ownText)

  const rewritten = !!e.rewriteText
  const body = rewritten && !original ? e.rewriteText! : e.ownText
  const lines = body.split('\n')
  const long = lines.length > CLIP_LINES || body.length > 1400
  const shown = open || !long ? body : lines.slice(0, CLIP_LINES).join('\n').slice(0, 1400)

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['voice-profile'] })
    void qc.invalidateQueries({ queryKey: ['voice-scenarios'] })
    void qc.invalidateQueries({ queryKey: ['voice-scenario-messages'] })
  }
  const save = useMutation({
    mutationFn: () => voiceApi.rewriteExemplar(spoke, e.id, text),
    onSuccess: () => { setEditing(false); setOriginal(false); refresh() },
  })
  const clear = useMutation({
    mutationFn: () => voiceApi.clearRewrite(spoke, e.id),
    onSuccess: () => { setEditing(false); setOriginal(false); refresh() },
  })
  const error = save.error ?? clear.error

  return (
    <blockquote className={`exemplar ${long && !open ? 'clipped' : ''} ${rewritten ? 'rewritten' : ''}`}>
      <p className="exemplar-meta">
        <span>{formatDate(e.sentAt)}</span>
        <span className="exemplar-subject">{e.subject || '(no subject)'}</span>
        <Correct kind="voice-scenario" entityId={e.id} entityLabel={e.subject ?? '(no subject)'} compact className="exemplar-correct" />
      </p>
      <p className="exemplar-why" title="Why this message was chosen">{e.reason}</p>

      {rewritten && (
        <p className="exemplar-rewritten">
          <span className="tag">rewritten by you · original kept</span>
          <button type="button" className="link-button" onClick={() => setOriginal((v) => !v)}>
            {original ? 'Show your rewrite' : 'Show what you sent'}
          </button>
        </p>
      )}

      {editing ? (
        <div className="exemplar-editor">
          <textarea
            className="exemplar-rewrite-box"
            rows={Math.min(18, Math.max(6, text.split('\n').length + 2))}
            value={text}
            onChange={(ev) => setText(ev.target.value)}
            aria-label="What you would rather have written"
          />
          <p className="muted small">
            Write it as you would rather have sent it. What you actually sent is kept; this is what a model is
            shown when it drafts for you here, and a big enough change becomes a preference on this scenario.
          </p>
          {error && <p className="error small">{error instanceof RefusedError ? error.refusal.message : (error as Error).message}</p>}
          <div className="exemplar-actions">
            <button type="button" className="button primary small" disabled={save.isPending || !text.trim()} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save the rewrite'}
            </button>
            <button type="button" className="button small" onClick={() => { setEditing(false); setText(e.rewriteText ?? e.ownText) }}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <pre>{shown}</pre>
          <div className="exemplar-actions">
            {long && (
              <button className="link-button exemplar-more" onClick={() => setOpen(!open)}>
                {open ? 'Show less' : `Show all ${lines.length} lines`}
              </button>
            )}
            <button type="button" className="link-button" onClick={() => { setText(e.rewriteText ?? e.ownText); setEditing(true) }}>
              {rewritten ? 'Edit your rewrite' : 'Rewrite'}
            </button>
            {rewritten && (
              <button type="button" className="link-button" disabled={clear.isPending} onClick={() => clear.mutate()}>
                {clear.isPending ? 'Clearing…' : 'Clear the rewrite'}
              </button>
            )}
          </div>
          {error && !editing && <p className="error small">{error instanceof RefusedError ? error.refusal.message : (error as Error).message}</p>}
        </>
      )}
    </blockquote>
  )
}
